import { useMemo } from 'react';

import type { EnrichmentAttributeGroup } from '~/components/common/enrichment-run-panel';
import { useEnrichmentAttributeGroups } from '~/components/common/enrichment-run-panel';

import { adaptMetaCatalog } from '../utils/adapt-meta-catalog';

import { useMetaCatalogAttributes } from './use-meta-catalog-attributes';

export interface ScheduledEnrichmentAttributeGroups {
  /** Attributes the operator can switch on/off. */
  groups: EnrichmentAttributeGroup[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * The run panel's attribute source for the scheduled flow, chosen by platform:
 *
 * - Google → the google agent's curated taxonomy
 *   (`GET google-agent/catalog/content-attributes`), a real selection list mapped onto
 *   `requested_fields`, plus the two post-steps that travel on `enrichment_config`.
 * - Meta → the meta agent's own catalogue (`GET meta-agent/catalog/attributes`),
 *   adapted to the same selectable shape (always-on fields as mandatory pills,
 *   `requested_fields` switches, plus the two post-step switches).
 *
 * Both queries are always mounted (hooks can't be conditional) but only the active
 * platform's is enabled, so exactly one request is made.
 */
export function useScheduledEnrichmentAttributeGroups(
  platform: 'google' | 'meta',
  enabled: boolean,
): ScheduledEnrichmentAttributeGroups {
  const isMeta = platform === 'meta';
  const google = useEnrichmentAttributeGroups(enabled && !isMeta);
  const meta = useMetaCatalogAttributes(enabled && isMeta);

  const adapted = useMemo(() => (meta.data ? adaptMetaCatalog(meta.data) : null), [meta.data]);

  if (!isMeta) return google;

  return {
    groups: adapted ?? [],
    isLoading: meta.isLoading,
    isError: meta.isError,
    refetch: () => {
      void meta.refetch();
    },
  };
}
